// deps
import React, { useCallback, useContext, useMemo, useState } from "react";

// helpers
import { isInstructionsHalfcardEnabled, isOptedRx } from "@cart-client/helpers/RxInstruction";
import { SESSION_STORAGE_KEYS, Storage } from "@swiggy-private/core-ui";
import { isEmpty } from "@swiggy-private/js-utils";
import { isClosingTimeAvailable } from "@cart-client/helpers/TimerOnCart";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// reducers
import { CartActionTypes } from "@cart-client/reducers/CartReducer";

// constants
import {
    RX_HALF_CARD_CTA,
    RX_INSTRUCTIONS_ASSETS,
    RX_INSTRUCTION_CHAR_LIMIT,
    TOAST_MESSAGES,
    EMOJI_REGEX,
    CHAR_LIMIT_ERROR_MESSAGE,
    RX_HALF_CARD,
} from "@cart-client/constants/RxInstructions";

// types and interfaces
import { BottomInputHalfCardProps } from "@swiggy-private/cart-ui";
import { COOKING_INSTRUCTION_INPUT_TEST_ID } from "@swiggy-private/cart-test-ids";
import { useRxInstructionsAnalytics } from "./useRxInstructionsAnalytics";

interface ToastDataVisible {
    show: true;
    icon: string;
    message: string;
}

interface ToastDataHidden {
    show: false;
}

type ToastData = ToastDataVisible | ToastDataHidden;

export interface RxHalfCardProps extends BottomInputHalfCardProps {
    isOpen: boolean;
    onClose: VoidFunction;
}

export interface UseRxInstructionReturnType {
    onSpecialInstructionsImpression: VoidFunction;
    onSpecialInstructionsClick: VoidFunction;
    handleRestaurantSuggestionChange: (note: string) => void;
    onRxHalfcardClose: VoidFunction;
    onRxInstructionClick: VoidFunction;
    isOptedInRx: boolean;
    isRxInstructionsHalfCardXPEnabled: boolean;
    showRxHalfCard: boolean;
    rxHalfcardProps: RxHalfCardProps;
    toastData: ToastData;
    isTimerOrCroutonAvailable: boolean;
    onToastDismiss: VoidFunction;
    note: string;
    textAreaActive: boolean;
    onButtonClick: VoidFunction;
    setNote: React.Dispatch<React.SetStateAction<string>>;
    setTextAreaActive: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useRxInstruction(isCroutonVisible: boolean, specialInstruction: string): UseRxInstructionReturnType {
    const { cartState, setCartState } = useContext(CartContext);
    const { reviewedCart: cartResponse, localCart } = cartState;
    const [showRxHalfCard, setRxHalfCard] = useState(false);
    const [note, setNote] = useState(specialInstruction);
    const [textAreaActive, setTextAreaActive] = useState(false);
    const [toastData, setToastData] = useState<ToastData>({
        show: false,
    });
    //state to store instruction input from bottom half card
    const [rxInstruction, setRxInstruction] = useState(specialInstruction);
    //state to store error for bottom input halfcard
    const [errorText, setErrorText] = useState("");

    const isOptedInRx = useMemo(() => cartResponse != null && isOptedRx(cartResponse.data), [cartResponse]);

    const isRxInstructionsHalfCardXPEnabled = useMemo(
        () => cartResponse != null && isInstructionsHalfcardEnabled(cartResponse.data),
        [cartResponse],
    );

    const cartId = useMemo(() => String(cartResponse?.data?.cartId || ""), [cartResponse]);

    const {
        onSpecialInstructionsImpression,
        onSpecialInstructionsClick,
        onSaveInstructions,
        onImpressionInstructionHalfCard,
        onClickInstructionHalfCardAddRequest,
        onClickInstructionHalfCardUpdateRequest,
        onClickInstructionHalfCardRemoveRequest,
        onClickInstructionHalfCardClose,
    } = useRxInstructionsAnalytics({
        cartResponse,
        note,
        cartId,
        isRxInstructionsHalfCardXPEnabled,
    });

    const handleRestaurantSuggestionChange = useCallback(
        (note: string) => {
            setCartState({
                type: CartActionTypes.UPDATE_LOCAL_CART,
                payload: { ...localCart, orderComment: note },
            });
            Storage.setItem(SESSION_STORAGE_KEYS.RX_SPECIAL_INSTRUCTIONS, note);
            onSaveInstructions(note);
        },
        [localCart, setCartState, onSaveInstructions],
    );

    const onRxInstructionClick = useCallback(() => {
        setRxHalfCard(true);
    }, []);

    const onRxHalfcardClose = useCallback(() => {
        setErrorText("");
        setRxHalfCard(false);
        setRxInstruction(note);
        onClickInstructionHalfCardClose();
    }, [note, onClickInstructionHalfCardClose]);

    const onRxHalfCardAddOrUpdateRequestClick = useCallback(() => {
        setRxHalfCard(false);
        setErrorText("");
        setToastData({
            show: true,
            icon: RX_INSTRUCTIONS_ASSETS.TOAST_TICK,
            message: isEmpty(note) ? TOAST_MESSAGES.ADD : TOAST_MESSAGES.UPDATE,
        });
        isEmpty(note) ? onClickInstructionHalfCardAddRequest() : onClickInstructionHalfCardUpdateRequest();
        handleRestaurantSuggestionChange(rxInstruction);
        setNote(rxInstruction);
    }, [
        handleRestaurantSuggestionChange,
        rxInstruction,
        onClickInstructionHalfCardAddRequest,
        onClickInstructionHalfCardUpdateRequest,
        note,
    ]);

    const onRxHalfCardRemoveClick = useCallback(() => {
        setRxHalfCard(false);
        setErrorText("");
        setRxInstruction("");
        setToastData({
            show: true,
            icon: RX_INSTRUCTIONS_ASSETS.TOAST_DELETE,
            message: TOAST_MESSAGES.REMOVE,
        });
        onClickInstructionHalfCardRemoveRequest();
        handleRestaurantSuggestionChange("");
        setNote("");
    }, [handleRestaurantSuggestionChange, onClickInstructionHalfCardRemoveRequest, setNote]);

    const onToastDismiss = useCallback(() => {
        setToastData({ show: false });
    }, []);

    const onRxHalfCardInputCharacterLimitExceeded = useCallback(() => {
        setErrorText(CHAR_LIMIT_ERROR_MESSAGE);
    }, []);

    const onRxHalfCardInputChange = useCallback(
        (value: string) => {
            const sanitizedValue = value.replace(EMOJI_REGEX, "");
            if (!!errorText && sanitizedValue.length < RX_INSTRUCTION_CHAR_LIMIT) {
                setErrorText("");
            }
            setRxInstruction(sanitizedValue);
        },
        [errorText],
    );

    const rxHalfcardProps: RxHalfCardProps = useMemo(
        () => ({
            isOpen: showRxHalfCard,
            onClose: onRxHalfcardClose,
            value: rxInstruction,
            onChange: onRxHalfCardInputChange,
            placeholder: RX_HALF_CARD.PLACEHOLDER,
            maxLength: RX_INSTRUCTION_CHAR_LIMIT,
            instructions: RX_HALF_CARD.INSTRUCTION,
            errorText,
            inputTestId: COOKING_INSTRUCTION_INPUT_TEST_ID,
            onLimitExceed: onRxHalfCardInputCharacterLimitExceeded,
            onPrimaryCtaClick: onRxHalfCardAddOrUpdateRequestClick,
            primaryCtaText: !note ? RX_HALF_CARD_CTA.ADD_REQUEST : RX_HALF_CARD_CTA.UPDATE,
            isPrimaryCtaDisabled: isEmpty(rxInstruction),
            onImpression: onImpressionInstructionHalfCard,
            ...(note && {
                secondaryCtaText: RX_HALF_CARD_CTA.REMOVE,
                onSecondaryCtaClick: onRxHalfCardRemoveClick,
            }),
        }),
        [
            showRxHalfCard,
            onRxHalfcardClose,
            rxInstruction,
            onRxHalfCardInputChange,
            errorText,
            onRxHalfCardInputCharacterLimitExceeded,
            onRxHalfCardAddOrUpdateRequestClick,
            note,
            onImpressionInstructionHalfCard,
            onRxHalfCardRemoveClick,
        ],
    );

    const onButtonClick = useCallback(() => {
        if (note) {
            handleRestaurantSuggestionChange?.("");
            setTextAreaActive(false);
            setNote("");
            return;
        }
        setTextAreaActive(true);
    }, [note, handleRestaurantSuggestionChange]);

    const isTimerOrCroutonAvailable = useMemo(() => {
        const isTimerExists = isClosingTimeAvailable(cartResponse);

        return isTimerExists || isCroutonVisible;
    }, [cartResponse, isCroutonVisible]);

    return {
        onSpecialInstructionsImpression,
        onSpecialInstructionsClick,
        handleRestaurantSuggestionChange,
        onRxHalfcardClose,
        onRxInstructionClick,
        onToastDismiss,
        isOptedInRx,
        isRxInstructionsHalfCardXPEnabled,
        showRxHalfCard,
        rxHalfcardProps,
        toastData,
        isTimerOrCroutonAvailable,
        note,
        textAreaActive,
        onButtonClick,
        setNote,
        setTextAreaActive,
    };
}
